import { useEffect, useRef, useState } from "react";
import { getJob } from "../api/client";

function parseProgress(log) {
  // Procura a última linha [PROG] N/TOTAL e [INFO] Total de SKUs a processar: N
  const progMatches = [...log.matchAll(/\[PROG\]\s+(\d+)\/(\d+)/g)];
  if (progMatches.length > 0) {
    const last = progMatches[progMatches.length - 1];
    return { current: parseInt(last[1], 10), total: parseInt(last[2], 10) };
  }
  // Fallback: só tem o total ainda (nenhum SKU processado)
  const totalMatch = log.match(/\[INFO\]\s+Total de SKUs a processar:\s+(\d+)/);
  if (totalMatch) {
    return { current: 0, total: parseInt(totalMatch[1], 10) };
  }
  return null;
}

function parseTally(log) {
  // Última linha [TALLY] processed=N created=N updated=N skipped=N errors=N —
  // contadores acumulados em tempo real (não precisa esperar o resumo final).
  const matches = [...log.matchAll(/\[TALLY\]\s+processed=(\d+)\s+created=(\d+)\s+updated=(\d+)\s+skipped=(\d+)\s+errors=(\d+)/g)];
  if (matches.length === 0) return null;
  const last = matches[matches.length - 1];
  return {
    processed: parseInt(last[1], 10),
    created: parseInt(last[2], 10),
    updated: parseInt(last[3], 10),
    skipped: parseInt(last[4], 10),
    errors: parseInt(last[5], 10),
  };
}

function parseReview(log) {
  // Marcador [REVISAO] — o EPEG travou esperando resolução manual de SKUs Funko
  // ambíguos (ver epeg/services/sku_resolver.py). A mensagem completa da linha vira
  // o texto exibido no aviso.
  const match = log.match(/\[REVISAO\].*/);
  return match ? match[0].replace("[REVISAO]", "").trim() : null;
}

function parsePlanilhaText(log) {
  // Bloco entre os marcadores impressos por epeg/main.py ao final da importação — o
  // texto pronto pra colar na aba SKUs da planilha. Precisa de uma caixa própria e
  // sempre visível: antes de existir [PROG] no log do EPEG (resolver de SKU Funko), o
  // log bruto nunca ficava recolhido por padrão, então esse texto aparecia direto. Com
  // [PROG] agora presente, hasStructuredSummary passou a recolher o log — sem essa caixa
  // dedicada, o texto ficava escondido atrás de "Ver log completo" (regressão real).
  const match = log.match(/=== TEXTO PARA COLAR NA ABA SKUs ===\n([\s\S]*?)\n=== FIM TEXTO PARA COLAR ===/);
  return match ? match[1] : null;
}

function CopyBox({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard indisponível (ex: contexto não-seguro) — usuário ainda pode selecionar manualmente
    }
  };
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-gray-700">Texto para colar na aba SKUs</span>
        <button
          type="button"
          onClick={copy}
          className="text-xs bg-blue-800 hover:bg-blue-700 text-white font-semibold px-3 py-1.5 rounded-lg"
        >
          {copied ? "✓ Copiado" : "Copiar"}
        </button>
      </div>
      <textarea
        readOnly
        value={text}
        rows={Math.min(10, text.split("\n").length + 1)}
        onFocus={(e) => e.target.select()}
        className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs font-mono text-gray-800 resize-y"
      />
    </div>
  );
}

function TallyBadge({ label, value, color }) {
  if (!value) return null;
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
      {label}: {value}
    </span>
  );
}

export default function JobLog({ jobId, onDone }) {
  const [log, setLog]           = useState("");
  const [status, setStatus]     = useState("queued");
  const [logExpanded, setLogExpanded] = useState(null); // null = ainda não decidido pelo usuário
  const preRef                  = useRef(null);
  const autoScroll              = useRef(true);

  // onDone pode ser recriado a cada render do componente pai (ex: se ele mesmo atualiza
  // estado dentro do callback, como o download automático do GNFF). Guardar num ref evita
  // que o efeito abaixo reinicie o polling e chame onDone de novo pro mesmo job já concluído.
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  useEffect(() => {
    if (!jobId) return;
    setLog("");
    setStatus("queued");
    setLogExpanded(null);
    autoScroll.current = true;

    const interval = status === "running" ? 1500 : 2000;

    const poll = setInterval(async () => {
      try {
        const { data } = await getJob(jobId);
        setLog(data.log || "");
        setStatus(data.status);
        if (data.status === "done" || data.status === "error") {
          clearInterval(poll);
          onDoneRef.current?.(data.status);
        }
      } catch {
        clearInterval(poll);
        setStatus("error");
        onDoneRef.current?.("error");
      }
    }, interval);

    return () => clearInterval(poll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    if (autoScroll.current && preRef.current) {
      preRef.current.scrollTop = preRef.current.scrollHeight;
    }
  }, [log]);

  if (!jobId) return null;

  const isActive = status === "queued" || status === "running";

  const headerColor = {
    queued:  "text-yellow-500",
    running: "text-blue-500",
    done:    "text-green-500",
    error:   "text-red-500",
  }[status] || "text-gray-400";

  const headerLabel = {
    queued:  "Na fila...",
    running: "Executando",
    done:    "Concluído",
    error:   "Erro",
  }[status] || status;

  const progress = log ? parseProgress(log) : null;
  const tally = log ? parseTally(log) : null;
  const reviewMsg = log ? parseReview(log) : null;
  const planilhaText = log ? parsePlanilhaText(log) : null;
  const pct = progress && progress.total > 0
    ? Math.round((progress.current / progress.total) * 100)
    : null;

  // Com resumo estruturado (progresso/contadores) disponível, o log bruto vira detalhe
  // opcional — some por padrão pra não pesar o navegador em execuções longas (ex: GDPB
  // --all com centenas de SKUs). Sem resumo estruturado, o log é a única informação
  // disponível, então continua visível por padrão.
  const hasStructuredSummary = Boolean(progress || tally);
  const showLog = logExpanded !== null ? logExpanded : !hasStructuredSummary;

  return (
    <div className="mt-5">
      {/* Header com status */}
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        {isActive && (
          <svg className="animate-spin h-4 w-4 text-blue-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
        )}
        {status === "done" && <span className="text-green-500">✓</span>}
        {status === "error" && <span className="text-red-500">✗</span>}
        <span className={`font-semibold text-sm ${headerColor}`}>{headerLabel}</span>
        {progress && (
          <span className="text-xs text-gray-500">
            {progress.current}/{progress.total} SKUs
            {pct !== null ? ` — ${pct}%` : ""}
          </span>
        )}
      </div>

      {/* Aviso de revisão manual pendente — EPEG travado esperando SKU Funko ambíguo */}
      {reviewMsg && (
        <div className="flex items-center gap-3 flex-wrap mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <span className="text-amber-800 text-sm">⚠ {reviewMsg}</span>
          <a
            href="/api/epeg/resolver/review"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap"
          >
            Abrir revisão de SKUs
          </a>
        </div>
      )}

      {/* Barra de progresso */}
      {isActive && (
        <div className="w-full h-2 bg-gray-200 rounded overflow-hidden mb-2">
          {pct !== null ? (
            <div
              className="h-2 bg-blue-500 rounded transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          ) : (
            <div className="h-2 bg-blue-500 rounded animate-progress" />
          )}
        </div>
      )}
      {status === "done" && progress && (
        <div className="w-full h-2 bg-green-200 rounded overflow-hidden mb-2">
          <div className="h-2 bg-green-500 rounded w-full" />
        </div>
      )}

      {/* Contadores em tempo real (Criados/Atualizados/Pulados/Erros) */}
      {tally && (
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <TallyBadge label="Criados" value={tally.created} color="bg-green-100 text-green-800" />
          <TallyBadge label="Atualizados" value={tally.updated} color="bg-blue-100 text-blue-800" />
          <TallyBadge label="Pulados" value={tally.skipped} color="bg-gray-100 text-gray-700" />
          <TallyBadge label="Erros" value={tally.errors} color="bg-red-100 text-red-800" />
        </div>
      )}

      {/* Texto pra colar na planilha (EPEG) — sempre visível, não depende do toggle do log */}
      {planilhaText && <CopyBox text={planilhaText} />}

      {/* Log em tempo real — recolhido por padrão quando já há resumo estruturado */}
      {hasStructuredSummary && (
        <button
          type="button"
          onClick={() => setLogExpanded(!showLog)}
          className="text-xs text-blue-700 hover:underline mb-2"
        >
          {showLog ? "▾ Ocultar log completo" : "▸ Ver log completo"}
        </button>
      )}

      {showLog && (
        <div className="relative">
          <pre
            ref={preRef}
            className="bg-gray-900 text-gray-100 text-xs p-4 rounded-lg overflow-auto max-h-80 whitespace-pre-wrap break-words"
            onScroll={(e) => {
              const el = e.currentTarget;
              const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
              autoScroll.current = atBottom;
            }}
          >
            {log || "Aguardando saída..."}
          </pre>
          {isActive && log && (
            <button
              type="button"
              onClick={() => {
                autoScroll.current = true;
                if (preRef.current) preRef.current.scrollTop = preRef.current.scrollHeight;
              }}
              className="absolute bottom-3 right-3 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded"
            >
              ↓ fim
            </button>
          )}
        </div>
      )}
    </div>
  );
}
